<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    NextButton,
    SpinnerLoader,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      qrImageUrl: null,
      sessionStatus: null,
      phoneNumber: null,
      isLoading: false,
      isSavingSignature: false,
      agentSignature: true,
      pollInterval: null,
    };
  },
  computed: {
    accountId() {
      return this.inbox.account_id || this.$route.params.accountId;
    },
    inboxId() {
      return this.inbox.id;
    },
    isConnected() {
      return this.sessionStatus === 'connected';
    },
    isQrReady() {
      return (
        this.sessionStatus === 'qr_ready' ||
        this.sessionStatus === 'pending'
      );
    },
    isFailed() {
      return (
        this.sessionStatus === 'failed' || this.sessionStatus === 'stopped'
      );
    },
    statusLabel() {
      const labels = {
        connected: 'Conectado',
        qr_ready: 'Aguardando QR Code',
        pending: 'Iniciando...',
        failed: 'Falha na conexão',
        stopped: 'Desconectado',
      };
      return labels[this.sessionStatus] || 'Carregando...';
    },
  },
  mounted() {
    this.fetchStatus();
    this.startPolling();
    this.agentSignature =
      this.inbox.additional_attributes?.waha_agent_signature !== false;
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async fetchStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/waha/status`
        );
        this.sessionStatus = data.session_status;
        this.phoneNumber = data.phone_number;

        if (this.isQrReady) {
          this.fetchQr();
        }
        if (this.isConnected) {
          this.qrImageUrl = null;
        }
      } catch (error) {
        console.error('Failed to fetch WAHA status:', error);
      }
    },
    async fetchQr() {
      try {
        const response = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/waha/qr.png`,
          { responseType: 'blob' }
        );
        if (response.data) {
          // Revoke previous URL to avoid memory leak
          if (this.qrImageUrl) URL.revokeObjectURL(this.qrImageUrl);
          this.qrImageUrl = URL.createObjectURL(response.data);
        }
      } catch (error) {
        this.qrImageUrl = null;
      }
    },
    async restartSession() {
      this.isLoading = true;
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/waha/restart`
        );
        useAlert('Sessão reiniciada. Aguarde o QR Code.');
        this.sessionStatus = 'pending';
        setTimeout(() => this.fetchStatus(), 5000);
      } catch (error) {
        useAlert('Erro ao reiniciar sessão');
      } finally {
        this.isLoading = false;
      }
    },
    async toggleSignature() {
      this.isSavingSignature = true;
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/waha/signature`,
          { enabled: this.agentSignature }
        );
        useAlert(
          this.agentSignature
            ? 'Assinatura do agente ativada'
            : 'Assinatura do agente desativada'
        );
      } catch (error) {
        useAlert('Erro ao atualizar assinatura');
        this.agentSignature = !this.agentSignature;
      } finally {
        this.isSavingSignature = false;
      }
    },
    startPolling() {
      this.pollInterval = setInterval(() => {
        this.fetchStatus();
      }, 5000);
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6 py-6">
    <!-- Status Banner -->
    <div
      class="flex items-center justify-between p-4 rounded-xl border border-n-weak bg-n-background"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-3 h-3 rounded-full"
          :class="{
            'bg-n-teal-11': isConnected,
            'bg-n-amber-11 animate-pulse': isQrReady,
            'bg-n-ruby-11': isFailed,
          }"
        />
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ statusLabel }}
          </p>
          <p v-if="phoneNumber" class="text-xs text-n-slate-11">
            {{ phoneNumber }}
          </p>
        </div>
      </div>
      <div class="flex gap-2">
        <NextButton
          v-if="isFailed || isConnected"
          :is-loading="isLoading"
          size="small"
          @click="restartSession"
          :label="isConnected ? 'Reconectar' : 'Tentar novamente'"
        />
      </div>
    </div>

    <!-- Agent Signature Toggle -->
    <div
      v-if="isConnected"
      class="flex items-center justify-between p-4 rounded-xl border border-n-weak bg-n-background"
    >
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          Assinatura do agente
        </p>
        <p class="text-xs text-n-slate-11">
          Exibe o nome do agente/DH antes de cada mensagem enviada no WhatsApp
        </p>
      </div>
      <label class="relative inline-flex items-center cursor-pointer">
        <input
          v-model="agentSignature"
          type="checkbox"
          class="sr-only peer"
          :disabled="isSavingSignature"
          @change="toggleSignature"
        />
        <div
          class="w-11 h-6 rounded-full peer bg-n-slate-6 peer-checked:bg-n-teal-9 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
        />
      </label>
    </div>

    <!-- QR Code Section -->
    <div
      v-if="isQrReady && qrImageUrl"
      class="flex flex-col items-center gap-4 p-8 rounded-xl border border-n-weak bg-n-background"
    >
      <h3 class="text-lg font-semibold text-n-slate-12">
        Escaneie o QR Code
      </h3>
      <p class="text-sm text-n-slate-11 text-center max-w-md">
        Abra o WhatsApp no seu celular, vá em
        <strong>Dispositivos conectados</strong> e escaneie o código abaixo.
      </p>
      <div class="p-4 bg-white rounded-xl shadow-lg">
        <img
          :src="qrImageUrl"
          alt="QR Code"
          class="w-64 h-64 object-contain"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        O QR Code atualiza automaticamente a cada 5 segundos
      </p>
    </div>

    <!-- Loading State -->
    <div
      v-if="isQrReady && !qrImageUrl"
      class="flex flex-col items-center gap-4 p-8 rounded-xl border border-n-weak bg-n-background"
    >
      <SpinnerLoader />
      <p class="text-sm text-n-slate-11">Gerando QR Code...</p>
    </div>

    <!-- Connected State -->
    <div
      v-if="isConnected"
      class="flex flex-col items-center gap-4 p-8 rounded-xl border border-n-teal-6 bg-n-teal-3"
    >
      <div
        class="w-16 h-16 rounded-full bg-n-teal-9 flex items-center justify-center"
      >
        <span class="text-white text-2xl">✓</span>
      </div>
      <h3 class="text-lg font-semibold text-n-teal-11">
        WhatsApp Conectado
      </h3>
      <p v-if="phoneNumber" class="text-sm text-n-teal-11">
        Número: {{ phoneNumber }}
      </p>
      <p class="text-sm text-n-slate-11 text-center max-w-md">
        Seu WhatsApp está conectado. As conversas aparecerão automaticamente
        nesta caixa de entrada.
      </p>
    </div>

    <!-- Failed State -->
    <div
      v-if="isFailed"
      class="flex flex-col items-center gap-4 p-8 rounded-xl border border-n-ruby-6 bg-n-ruby-3"
    >
      <h3 class="text-lg font-semibold text-n-ruby-11">
        Conexão perdida
      </h3>
      <p class="text-sm text-n-ruby-11 text-center max-w-md">
        A sessão do WhatsApp foi desconectada. Clique em "Tentar novamente"
        para reconectar.
      </p>
    </div>
  </div>
</template>
